<template>
  <MyProfileModal
    title="Mon profil"
    :activatorLabel="buttonLabel"
    :activatorIcon="buttonIcon"
    @close="onClose"
    @open="onOpen"
    size="lg"
  >
    <template v-if="slots.activator" #activator="slotData">
      <slot name="activator" v-bind="slotData" />
    </template>

    <template #title="{ title }">
      <div class="flex items-center">
        <h1 class="text-h4 font-bold mb-2">
          {{ title }}
        </h1>
        <SaveStatus :loading="saving || uploading" />
      </div>
    </template>

    <PTabs v-model="tab" class="mt-6 h-[24rem]">
      <PTab name="notifications" label="Notifications" data-sentry-id="PrefNotifTab">
        <div class="pb-6 px-1">
          <PCheckboxGroup
            :model-value="enabledMailTypes"
            @update:model-value="onUpdateEnabledMailTypes"
            label="Je reçois une notification par mél lorsque"
            data-sentry-id="PrefMailTypesCheckboxGroup"
          />
          <PSelect
            :label="`Notifications par mél sur ${currentUser.login}`"
            :options="options"
            :model-value="mailingFrequency"
            @update:model-value="onUpdateMailingFrequency"
            :prepend-icon="mailIcon"
            hide-messages
            class="mb-1v"
            data-sentry-id="PrefMailFrequencySelect"
          ></PSelect>
          <p v-for="(msg, index) of mailingFrequencyMessages" :key="index">
            {{ msg }}
          </p>
        </div>
      </PTab>
      <PTab name="signatures" label="Signatures" class="[&>*]:w-full" data-sentry-id="PrefSignaturesTab">
        <div class="items-start">
          <div class="pr-2">
            <div class="flex gap-1">
              <OhVueIcon :name="attachmentIcon" class="mt-1v" />
              <p class="text-grey-50 mb-2">Ajoutez votre griffe</p>
            </div>
            <Dropfile
              v-if="!uploading"
              accept="image/*"
              class="mt-1 px-2 py-2 flex flex-col items-center justify-center border-dotted rounded-md border-2"
              :class="{
                'text-grey-425 border-grey-925': !dragging,
                'text-grey-50 border-grey-50': dragging,
              }"
              @input="addSignatures"
              @dragging="dragging = $event"
            >
              <span>Déposez une image (jpeg, png...)</span>
            </Dropfile>
            <Progressbar v-else />
          </div>
          <div v-if="signatures.length" class="pl-2 mt-2 flex flex-col">
            <p class="text-grey-50 mb-2">Signatures disponibles</p>
            <div class="flex flex-wrap justify-start gap-4">
              <div
                v-for="(signature, index) in signatures"
                :key="index"
                class="w-1/5 h-12 flex items-end border border-dashed relative"
              >
                <img
                  :src="signature.src"
                  :alt="`signature ${signature.id || index}`"
                  class="object-contain w-full h-full"
                />
                <PButton
                  icon
                  mode="tertiary-borderless"
                  @click="deleteSignature(index)"
                  class="absolute bottom-[2px] right-[2px] hover:bg-grey-625/30"
                  :data-sentry-id="`PrefSignatureRemove-${index}`"
                >
                  <span class="sr-only"> supprimer la signature {{ index + 1 }} </span>
                  <OhVueIcon :name="deleteIcon" role="img" />
                </PButton>
                <PTooltip
                  label="cliquez pour définir cette signature par défaut"
                  :delay="500"
                  :offset="5"
                  class="absolute top-[2px] right-[2px]"
                  :disabled="signatures.length === 1 || signature.default"
                >
                  <PButton
                    icon
                    mode="tertiary-borderless"
                    @click="signatures.length === 1 ? noop() : setAsDefault(index)"
                    class="hover:bg-grey-625/30"
                    :class="{ 'cursor-default': signature.default }"
                    :data-sentry-id="`PrefSignatureDefaultBtn-${index}`"
                  >
                    <span class="sr-only"> définir la signature {{ index + 1 }} par défaut </span>
                    <OhVueIcon :name="signature.default ? starIconFill : starIconLine" role="img" />
                  </PButton>
                </PTooltip>
              </div>
            </div>
          </div>
          <div class="p-2 flex gap-2">
            <div class="w-1/2">
              <PInput
                label="Fonction"
                v-model="job"
                @blur="updateJob"
                hide-messages
                data-sentry-id="PrefFunctionInput"
              />
            </div>
            <div class="w-1/2">
              <PInput label="Ville" v-model="city" @blur="updateCity" hide-messages data-sentry-id="PrefCityInput" />
            </div>
          </div>
        </div>

        <div class="p-2">
          <p>Dimensions du rectangle de prépositionnement</p>

          <p class="text-sm text-grey-425 mb-1">
            Sélectionnez un utilisateur pour importer les dimensions de sa signature
          </p>
          <SelectUser
            data-sentry-id="AddFluxUserInput"
            ref="selectUserEl"
            aria-label="Sélectionner un utilisateur pour importer les dimensions de sa signature"
            v-model="agentsDirectoryEntry"
            @update:model-value="onSelectUser"
            hide-messages
            with-c-c
          />

          <div
            v-if="userSignaturesDimensions.length"
            class="flex flex-wrap gap-2 mt-2"
            aria-live="polite"
            aria-label="Signatures disponibles"
          >
            <PButton
              v-for="(dim, index) in userSignaturesDimensions"
              :key="index"
              mode="secondary"
              @click="applySignatureDimensions(dim)"
              :aria-label="`Appliquer les dimensions de la signature ${index + 1} : ${dim.width} pixels de large sur ${
                dim.height
              } pixels de haut`"
            >
              Signature {{ index + 1 }} ({{ dim.width }}×{{ dim.height }})
            </PButton>
          </div>
          <div v-if="signatureDimentsionsError" class="flex justify-center py-1">
            {{ signatureDimentsionsError }}
          </div>

          <div class="flex gap-2 mt-2">
            <PInput
              v-model="prepoWidth"
              @blur="updatePrepoWidth"
              label="Largeur"
              type="number"
              :min="MIN_PREPOSITION_PIXELS"
              aria-label="Largeur du rectangle de prépositionnement"
            />
            <PInput
              v-model="prepoHeight"
              @blur="updatePrepoHeight"
              label="Hauteur"
              type="number"
              :min="MIN_PREPOSITION_PIXELS"
              aria-label="Hauteur du rectangle de prépositionnement"
            />
          </div>
          <div v-if="prepoSizeError" class="py-1">
            {{ prepoSizeError }}
          </div>
        </div>
      </PTab>
      <PTab name="export" label="Export" data-sentry-id="PrefExportTab">
        <div class="px-1">
          <p>Je télécharge les parapheurs du</p>
          <div class="flex gap-2 items-center">
            <PDateTimePicker
              label=""
              v-model="exportStartDate"
              :disabled-dates="getDisallowedStartDates"
              aria-label="Date de début: format jj/mm/aaaa"
              :teleport="true"
              data-sentry-id="PrefExportStartDatePicker"
            />
            <span class="mb-3 text-sm">au</span>
            <PDateTimePicker
              label=""
              v-model="exportEndDate"
              :disabled-dates="getDisallowedEndDates"
              aria-label="Date de fin: format jj/mm/aaaa"
              :teleport="true"
              data-sentry-id="PrefExportEndDatePicker"
            />
          </div>
          <div class="w-full flex flex-col items-end">
            <PButton class="mb-1" @click="startExport" :disabled="!canStartExport" data-sentry-id="PrefExportStartBtn">
              Télécharger mes parapheurs
            </PButton>
            <p class="text-grey-200 italic">Sous 24h, vous recevrez un lien par mél pour accéder à l'archive</p>
          </div>
        </div>
      </PTab>
      <PTab name="changementPoste" label="Changement de poste" data-sentry-id="PrefChangeJobTab">
        <div class="px-1">
          <div class="flex gap-2">
            <p class="shrink-0 mt-2 font-bold text-lg">Ma mobilité a eu lieu le</p>
            <PDateTimePicker
              label=""
              v-model="hideLetterFilesBefore"
              aria-label="Saisir une date de changement de poste: Format jj/mm/aaaa"
              class="text-sm"
              :teleport="true"
              :disabled-dates="isAfterToday"
              no-clear
              data-sentry-id="PrefHidelfbeforeDatePicker"
            />
          </div>
          <p class="mb-2">
            La déclaration de changement de poste est définitive et ne pourra pas être modifiée par la suite.
          </p>
          <p>
            Dès validation de votre date de mobilité, vous ne pourrez plus accéder aux parapheurs antérieurs à cette
            date.
          </p>
          <div class="flex flex-col items-stretch gap-2 mt-4 px-8">
            <PTooltip
              :offset="5"
              :label="
                canStartExport
                  ? `L'archive contenant vos parapheurs vous sera envoyée par mél`
                  : 'Option indisponible car un export est déjà en cours'
              "
              :disabled="!isDirty"
            >
              <PButton
                @click="saveHideBeforeAndDl()"
                :disabled="!canStartExport || !isDirty"
                class="w-full"
                data-sentry-id="PrefHidelfbeforeSaveAndDLBtn"
              >
                Je valide ma date de mobilité et je reçois l'archive contenant mes parapheurs par mél
              </PButton>
            </PTooltip>
            <PButton
              color="error"
              mode="secondary"
              @click="saveHideBeforeNoDl()"
              :disabled="!isDirty"
              data-sentry-id="PrefHidelfbeforeSaveNoDLBtn"
            >
              Je valide ma date de mobilité sans télécharger mes parapheurs
            </PButton>
          </div>
        </div>
      </PTab>
      <PTab name="couleur" label="Couleur" data-sentry-id="PrefColorTab">
        <div class="pb-16 px-1">
          <PSelect
            label="Couleur des parapheurs"
            :options="colorOptions"
            :model-value="color"
            @update:model-value="onUpdateColor"
            :prepend-icon="colorIcon"
            hide-messages
            class="mb-1v"
            :data-sentry-id="`PrefColorSelect-${color}`"
          ></PSelect>
        </div>
      </PTab>
      <PTab name="badges" label="Badges" data-sentry-id="PrefTagTab">
        <div class="pb-6 px-1">
          <div class="mb-3 italic">
            Vous ne pouvez activer que
            {{ userPreferencesStore.maxActivatedTags }} badges au maximum.
          </div>

          <div class="grid grid-cols-3 gap-2 mb-2">
            <PCheckbox
              v-for="(tagEnabled, index) in enabledTagTypes"
              :key="tagEnabled.value"
              :disabled="hasMaxTagsSelected && !tagEnabled.selected"
              :label="tagEnabled.label"
              :model-value="tagEnabled.selected"
              @update:model-value="onUpdateEnabledTagTypes(index, $event)"
              data-sentry-id="PrefTagTypesCheckboxGroup"
              hide-messages
            />
          </div>

          <p>
            Si vous ne trouvez pas le badge que vous recherchez, vous devez d'abord faire une demande de création à
            <PLink :to="`mailto:${config.contact}`">{{ config.contact }}</PLink>
            . Avec en objet "création de badge". Le badge apparaitra dans la liste sous 24 heures.
          </p>
        </div>
      </PTab>
      <PTab name="absence" label="Absence" data-sentry-id="PrefAbsenceTab">
        <div class="px-1">
          <PDateTimePicker
            :label="`${genderize('Absent')} à partir du`"
            :model-value="absentFrom"
            @update:model-value="onUpdateAbsenceDate($event as string, 'absentFrom')"
            class="text-sm"
            :disabled-dates="isBeforeToday"
            :teleport="true"
            data-sentry-id="PrefAbsentFromDatePicker"
          />

          <PDateTimePicker
            :label="`${genderize('Absent')} jusqu’au`"
            :model-value="absentUntil"
            @update:model-value="onUpdateAbsenceDate($event as string, 'absentUntil')"
            class="text-sm"
            :disabled-dates="isBeforeAbsentFrom"
            :teleport="true"
            data-sentry-id="PrefAbsentUntilDatePicker"
            :help="`Cette date sera indiquée dans la bulle d’aide présente à gauche de votre nom dans les flux. Si aucune date n’est saisie, il y aura juste la mention &quot;est ${genderize('absent')}&quot;.`"
          />
        </div>
      </PTab>
    </PTabs>
  </MyProfileModal>
</template>

<script setup lang="ts">
import {
  AlertDuration,
  AlertType,
  type CheckboxOption,
  PButton,
  PCheckbox,
  PCheckboxGroup,
  PDateTimePicker,
  PInput,
  PLink,
  PSelect,
  PTab,
  PTabs,
  PTooltip,
  useAlertManager,
  useConfig,
  useIcons,
} from "@pasta/box-v2";
import { MyProfileModal, useAuthStore } from "@pasta/front-auth-v2";
import dayjs from "dayjs";
import { noop } from "lodash";
import { OhVueIcon } from "oh-vue-icons";
import { RiAttachment2, RiDeleteBin6Line, RiMailLine, RiPaletteLine, RiStarFill, RiStarLine } from "oh-vue-icons/icons";
import { computed, inject, onMounted, ref, type Slots, useSlots, watch } from "vue";
import { routeLocationKey, routerKey } from "vue-router";
import xbytes from "xbytes";
import { type AgentsDirectoryAutocompleteItem } from "../dto/agents-directory.dto";
import { type RequestExportDto } from "../dto/letter-file.dto";
import { type SignatureObjectDto } from "../dto/signature.dto";
import { LetterFileColor, MailingFrequency } from "../dto/user-preferences.dto";
import { useFileApi } from "../http/file.api";
import { useEnhanceSignatureStore } from "../stores/enhance-signature.store";
import { useExportStore } from "../stores/export.store";
import { useLetterFileTagStore } from "../stores/letter-file-tag.store";
import { useLetterFileStore } from "../stores/letter-file.store";
import { useMassSignatureStore } from "../stores/mass-signature.store";
import { useUserPreferencesStore } from "../stores/user-preferences.store";
import { NotificationType, type ParapheurConfig } from "../types";
import Dropfile from "../views/LetterFileForm/Documents/Dropfile.vue";
import SaveStatus from "../views/LetterFileForm/SaveStatus.vue";
import Progressbar from "./Progressbar.vue";
import SelectUser from "./SelectUser.vue";
import { useGenderize } from "../utils/gender";
import { MIN_PREPOSITION_PIXELS } from "../utils/signature-size.ts";

interface Props {
  defaultTab?: string;
  buttonLabel?: string;
  buttonIcon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultTab: "notifications",
  buttonLabel: "Voir mon profil",
});

const slots: Slots = useSlots();
defineSlots<{ activator: (props: { open: () => void }) => any }>();

const router = inject(routerKey, null);
const route = inject(routeLocationKey, null);
const userPreferencesStore = useUserPreferencesStore();
const storeHideLetterFilesBefore = computed(() => userPreferencesStore.hideLetterFilesBefore || "");
const letterFileStore = useLetterFileStore();
const authStore = useAuthStore();
const massSignatureStore = useMassSignatureStore();
const tagsStore = useLetterFileTagStore();
const genderize = useGenderize();
const currentUser = computed(() => authStore.currentUser!);
const [mailIcon, attachmentIcon, deleteIcon, colorIcon, starIconLine, starIconFill] = useIcons(
  RiMailLine,
  RiAttachment2,
  RiDeleteBin6Line,
  RiPaletteLine,
  RiStarLine,
  RiStarFill
);

const fileApi = useFileApi();
const config = useConfig<ParapheurConfig>();
const alertManager = useAlertManager();
const tab = ref<string>(props.defaultTab);
const enhanceSignatureStore = useEnhanceSignatureStore();
const saving = ref(false);

const enabledTagTypes = ref<CheckboxOption[]>([]);
const enabledMailTypes = ref<CheckboxOption[]>([
  {
    label: "je reçois un parapheur à mon niveau",
    selected: true,
    value: NotificationType.AT_YOUR_LEVEL,
  },
  {
    label: "je suis mis en copie d'un parapheur",
    selected: true,
    value: NotificationType.CC,
  },
  {
    label: "un de mes parapheurs est archivé ou supprimé",
    selected: false,
    value: NotificationType.ARCHIVED_OR_DELETED,
  },
  {
    label: "un de mes parapheurs est signé",
    selected: false,
    value: NotificationType.SIGNED,
  },
  {
    label: "un de mes parapheurs est en retard",
    selected: false,
    value: NotificationType.DEADLINE,
  },
  {
    label: "un commentaire a été ajouté à un de mes parapheurs",
    selected: false,
    value: NotificationType.COMMENT,
  },
]);
const mailingFrequency = ref<MailingFrequency | null>(null);
const options = [
  { label: "Aucune notification", value: MailingFrequency.NONE },
  { label: "1 fois par jour", value: MailingFrequency.ONCE_A_DAY },
  { label: "2 fois par jour", value: MailingFrequency.TWICE_A_DAY },
  { label: "En temps réel", value: MailingFrequency.REAL_TIME },
];
const hideLetterFilesBefore = ref<string>("");
const color = ref<LetterFileColor>(LetterFileColor.GREEN);
const colorOptions = [
  { label: "Bleu", value: LetterFileColor.BLUE },
  { label: "Vert", value: LetterFileColor.GREEN },
  { label: "Rouge", value: LetterFileColor.RED },
  { label: "Jaune", value: LetterFileColor.YELLOW },
];
const job = ref<string>("");
const city = ref<string>("");
const prepoWidth = ref<string>("");
const prepoHeight = ref<string>("");
const agentsDirectoryEntry = ref<AgentsDirectoryAutocompleteItem[]>([]);
const signatureDimentsionsError = ref("");
const prepoSizeError = ref("");
const userSignaturesDimensions = ref<{ width: number; height: number }[]>([]);
const absentFrom = ref<string>("");
const absentUntil = ref<string>("");

const hasMaxTagsSelected = computed(() => {
  return enabledTagTypes.value.filter(t => t.selected).length >= userPreferencesStore.maxActivatedTags;
});

const mailingFrequencyMessages = computed(() => {
  switch (mailingFrequency.value) {
    case MailingFrequency.NONE:
      return ["Aucune notification ne vous sera transmise par l'application"];
    case MailingFrequency.ONCE_A_DAY:
      return [
        "Une notification vous sera transmise uniquement si vous recevez un (ou des) nouveau(x) parapheur(s) à votre niveau.",
      ];
    case MailingFrequency.TWICE_A_DAY:
      return [
        "Une notification vous sera transmise uniquement si vous recevez un (ou des) nouveau(x) parapheur(s) à votre niveau.",
        "La notification mél sera envoyée à 10h et à 15h",
      ];
    case MailingFrequency.REAL_TIME:
      return [
        "Une notification vous sera transmise uniquement si vous recevez un nouveau parapheur à votre niveau.",
        "La notification mél vous sera envoyée dès réception d'un parapheur",
      ];
    default:
      return [];
  }
});

onMounted(async () => {
  mailingFrequency.value = userPreferencesStore.mailingFrequency;
  hideLetterFilesBefore.value = storeHideLetterFilesBefore.value || "";
  color.value = userPreferencesStore.color;
  job.value = userPreferencesStore.job;
  city.value = userPreferencesStore.city;
  prepoWidth.value = userPreferencesStore.prepoWidth?.toString() ?? "";
  prepoHeight.value = userPreferencesStore.prepoHeight?.toString() ?? "";
  for (const enabledMailType of enabledMailTypes.value) {
    enabledMailType.selected = userPreferencesStore.enabledMailTypes.includes(enabledMailType.value);
  }
  absentFrom.value = userPreferencesStore.absentFrom ?? "";
  absentUntil.value = userPreferencesStore.absentUntil ?? "";

  await tagsStore.fetchList({
    page: 1,
    pageSize: 100,
  });
});

async function notifySaving(saveFn: Function) {
  saving.value = true;
  try {
    await saveFn();
  } catch {
    alertManager.createAlert({
      message: "Une erreur est survenue, veuillez rafraichir la page",
      type: AlertType.ERROR,
    });
  } finally {
    saving.value = false;
  }
}

async function onUpdateEnabledMailTypes($event: CheckboxOption[]) {
  enabledMailTypes.value = $event;
  await notifySaving(() =>
    userPreferencesStore.updateMine({
      enabledMailTypes: enabledMailTypes.value.filter(item => item.selected).map(item => item.value),
    })
  );
}

async function onUpdateMailingFrequency($event: MailingFrequency) {
  mailingFrequency.value = $event;
  await userPreferencesStore.updateMine({
    mailingFrequency: mailingFrequency.value || undefined,
  });
}

async function onUpdateColor($event: LetterFileColor) {
  color.value = $event;
  await notifySaving(() => userPreferencesStore.updateMine({ color: color.value }));
}

async function onUpdateEnabledTagTypes(index: number, value: boolean) {
  const found = enabledTagTypes.value.at(index);
  found!.selected = value;
  await notifySaving(() =>
    userPreferencesStore.updateMine({
      tags: enabledTagTypes.value.filter(item => item.selected).map(item => item.value),
    })
  );
}

async function updateJob() {
  if (userPreferencesStore.job !== job.value) {
    await notifySaving(() => userPreferencesStore.updateMine({ job: job.value }));
  }
}

async function updateCity() {
  if (userPreferencesStore.city !== city.value) {
    await notifySaving(() => userPreferencesStore.updateMine({ city: city.value }));
  }
}

async function onUpdateAbsenceDate($event: string, date: "absentFrom" | "absentUntil") {
  const target = date === "absentFrom" ? absentFrom : absentUntil;
  target.value = $event;

  if (absentFrom.value && absentUntil.value && dayjs(absentFrom.value).isAfter(absentUntil.value, "day")) {
    absentUntil.value = "";
  }

  await notifySaving(() =>
    userPreferencesStore.updateMine({
      absentFrom: absentFrom.value || null,
      absentUntil: absentUntil.value || null,
    })
  );
}

// Signatures
const signatures = ref<(SignatureObjectDto & { src?: string })[]>([]);
const dragging = ref<boolean>(false);
const uploading = ref<boolean>(false);

async function addSignatures(files: File[]) {
  if (files.length + signatures.value.length > config.signatures.max) {
    alertManager.createAlert({
      message: `Nombre de signature maximum dépassé (${config.signatures.max} max)`,
      duration: AlertDuration.MEDIUM,
      type: AlertType.WARNING,
    });
    return;
  }

  await notifySaving(async () => {
    const fileIds = await uploadFiles(files);
    await userPreferencesStore.updateSignObjects([
      ...fileIds.map((fileId, index) => ({
        fileId,
        default: !signatures.value.some(item => item.default) && index === files.length - 1,
      })),
      ...signatures.value,
    ]);
    await refreshSignatures();
  });
  enhanceSignatureStore.$reset();
}

async function uploadFiles(files: File[]): Promise<number[]> {
  const sizeLimit = xbytes.parseSize(config.signatures.fileSizeLimit) || 5 * 1024 * 1024;

  const fileIds: number[] = [];
  try {
    uploading.value = true;
    for (const file of files) {
      const extension = (file.name.split(".").at(-1) || "").toLowerCase();
      if (!config.signatures.extensions.includes(extension)) {
        alertManager.createAlert({
          message: `extension de fichier ".${extension}" non acceptée`,
          duration: AlertDuration.INFINITE,
          type: AlertType.WARNING,
        });
      } else if (file.size > sizeLimit) {
        alertManager.createAlert({
          message: `${file.name} est trop volumineux et n'a pas été traité (maximum: ${xbytes(sizeLimit).replaceAll(
            "B",
            "o"
          )})`,
          duration: AlertDuration.INFINITE,
          type: AlertType.WARNING,
        });
      } else {
        const form = new FormData();
        form.append("file", file);
        fileIds.push((await fileApi.uploadFile(form, "upload-signature")).id);
      }
    }
  } catch {
    alertManager.createAlert({
      message: "Le téléversement a échoué",
      duration: AlertDuration.INFINITE,
      type: AlertType.ERROR,
    });
  } finally {
    uploading.value = false;
  }
  return fileIds;
}

async function refreshSignatures() {
  signatures.value = [];
  for (const signObject of userPreferencesStore.signatureObjects) {
    signatures.value = [
      ...signatures.value,
      {
        ...signObject,
        src: await userPreferencesStore.getSignatureLink(signObject.id),
      },
    ];
  }
  if (signatures.value.length === 0) {
    massSignatureStore.clearSelection();
  }
}

async function onOpen() {
  tab.value = props.defaultTab;
  await refreshSignatures();

  enabledTagTypes.value = [];
  for (const tag of tagsStore.list.data) {
    enabledTagTypes.value.push({
      label: tag.name,
      selected: !!userPreferencesStore.tags?.find(item => item.id === tag.id),
      value: tag.id,
    });
  }
}

function onClose() {
  signatures.value = userPreferencesStore.signatureObjects;
  tab.value = "notifications";

  exportStartDate.value = "";
  exportEndDate.value = "";

  signatureDimentsionsError.value = "";
  userSignaturesDimensions.value = [];

  hideLetterFilesBefore.value = storeHideLetterFilesBefore.value;
}

async function deleteSignature(index: number) {
  await notifySaving(async () => {
    await userPreferencesStore.updateSignObjects(signatures.value.filter((_item, i) => i !== index));
    await refreshSignatures();
  });
}

async function setAsDefault(at: number) {
  signatures.value.forEach((signature, index) => {
    signature.default = index === at;
  });
  await notifySaving(() => userPreferencesStore.updateSignObjects(signatures.value));
}

async function updatePrepoWidth() {
  await updatePrepoSize("prepoWidth", prepoWidth.value);
}

async function updatePrepoHeight() {
  await updatePrepoSize("prepoHeight", prepoHeight.value);
}

async function updatePrepoSize(property: "prepoWidth" | "prepoHeight", value: string) {
  const pixels = Number.parseInt(value);
  if (userPreferencesStore[property] === pixels) {
    return;
  }
  if (pixels < MIN_PREPOSITION_PIXELS) {
    prepoSizeError.value = `La valeur saisie doit être d'au moins ${MIN_PREPOSITION_PIXELS} pixels`;
    return;
  }
  prepoSizeError.value = "";
  await notifySaving(() => userPreferencesStore.updateMine({ [property]: pixels }));
}

async function onSelectUser($event: any) {
  signatureDimentsionsError.value = "";
  if ($event.length === 0 || !$event[0].mail) {
    userSignaturesDimensions.value = [];
    return;
  }
  userSignaturesDimensions.value = (await userPreferencesStore.getSignaturesDimensions($event[0].mail)) ?? [];

  if (userSignaturesDimensions.value.length === 0) {
    signatureDimentsionsError.value = "Aucune signature trouvée";
  }
}

async function applySignatureDimensions(dim: { width: number; height: number }) {
  prepoWidth.value = dim.width.toString();
  prepoHeight.value = dim.height.toString();
  await notifySaving(() =>
    userPreferencesStore.updateMine({
      prepoWidth: dim.width,
      prepoHeight: dim.height,
    })
  );
}

// export
const exportStore = useExportStore();
const canStartExport = computed(() => exportStore.canStartExport);
const exportStartDate = ref<string>("");
const exportEndDate = ref<string>("");

function getDisallowedStartDates(val: Date) {
  if (!exportEndDate.value) return dayjs(val).isAfter(dayjs());
  return dayjs(val).isAfter(exportEndDate.value) || dayjs(val).isAfter(dayjs());
}

function getDisallowedEndDates(val: Date) {
  if (!exportStartDate.value) return dayjs(val).isAfter(dayjs());
  return dayjs(val).isBefore(exportStartDate.value) || dayjs(val).isAfter(dayjs());
}

watch(tab, async () => {
  if (tab.value === "signatures") {
    job.value = userPreferencesStore.job;
    city.value = userPreferencesStore.city;
  }
  if (["export", "changementPoste"].includes(tab.value)) {
    await exportStore.fetchCanStartExport();
  }
});

async function startExport() {
  const body: RequestExportDto = {
    withDocuments: true,
  };
  if (exportStartDate.value) body.startDate = exportStartDate.value;
  if (exportEndDate.value) body.endDate = exportEndDate.value;
  const currentExport = await exportStore.startExport(body);
  await exportStore.poll(currentExport.id);
}

// changement de poste
const isDirty = computed<boolean>(() => {
  return (
    dayjs(hideLetterFilesBefore.value).format("DD/MM/YYYY") !==
    dayjs(storeHideLetterFilesBefore.value).format("DD/MM/YYYY")
  );
});

async function saveHideBefore() {
  const oldHideLetterFilesBefore = storeHideLetterFilesBefore.value;

  await userPreferencesStore.updateMine({
    hideLetterFilesBefore: hideLetterFilesBefore.value
      ? dayjs(hideLetterFilesBefore.value)
          .utcOffset(0)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .set("millisecond", 0)
          .toISOString()
      : null,
  });
  // If the user changed job, we need to reload the letter files
  if (oldHideLetterFilesBefore !== storeHideLetterFilesBefore.value) {
    if (route?.name !== "home") {
      await router?.push({ name: "home" });
    }
    await letterFileStore.getHome();
  }
}

async function saveHideBeforeAndDl() {
  await exportStore.startExport({ withDocuments: true });
  await saveHideBefore();
}

async function saveHideBeforeNoDl() {
  await saveHideBefore();
}

function isAfterToday(date: Date) {
  return dayjs(date).isAfter(dayjs(), "day");
}

function isBeforeToday(date: Date) {
  return dayjs(date).isBefore(dayjs(), "day");
}

function isBeforeAbsentFrom(date: Date) {
  if (absentFrom.value) {
    return dayjs(date).isBefore(dayjs(absentFrom.value), "day");
  }
  return isBeforeToday(date);
}
</script>
